import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { track, trackPurchase, trackMetaEventWithId } from "../lib/analytics";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Checkbox } from "../components/ui/checkbox";
import { ShieldAlert, Lock, Gift, Check } from "lucide-react";
import { getReferral } from "../lib/referral";
import { formatPercentOff } from "../lib/format";
import { FEE_DEFAULT } from "../lib/appDefaults";

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const loadZoho = () => new Promise((resolve) => {
  if (window.ZPayments) return resolve(true);
  const s = document.createElement("script");
  s.src = "https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js";
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

export default function Payment() {
  const navigate = useNavigate();
  const { user, refresh, config } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState(() => getReferral() || "");
  const [refBusy, setRefBusy] = useState(false);
  const [perk, setPerk] = useState(null);
  const fee = config?.entry_fee ?? FEE_DEFAULT;
  const finalPrice = perk?.valid ? perk.final_price : fee;
  const isFree = perk?.valid && perk.final_price <= 0;

  const applied = !!user?.referral_code;

  // Live discounted-price preview (server recomputes; never trust client price).
  const loadPerk = async (codeOverride) => {
    const code = (codeOverride ?? (user?.referral_code || refCode) ?? "").trim();
    if (!code) { setPerk(null); return; }
    try {
      const { data } = await api.get(`/payment/perk?code=${encodeURIComponent(code)}`);
      setPerk(data);
    } catch { setPerk(null); }
  };
  useEffect(() => { if (user?.referral_code) loadPerk(user.referral_code); }, [user?.referral_code]); // eslint-disable-line

  const applyReferral = async () => {
    if (!refCode.trim()) return toast.error("Enter a referral code first");
    setRefBusy(true);
    try {
      await api.post("/auth/apply-referral", { referral_code: refCode.trim() });
      await loadPerk(refCode.trim());
      toast.success("Referral code applied!");
      await refresh();
    } catch (err) { toast.error(errorMessage(err)); } finally { setRefBusy(false); }
  };

  // GA4 begin_checkout — fire once when an unpaid user lands on the pay screen.
  // Paired with `purchase`, this measures checkout-abandonment.
  const checkoutFired = useRef(false);
  useEffect(() => {
    if (checkoutFired.current) return;
    if (user && user.payment_status !== "paid") {
      checkoutFired.current = true;
      track("begin_checkout", { funnel_step: 3, value: fee, currency: "INR" });
    }
  }, [user, fee]);

  // If the user has already paid, get them to their team page immediately —
  // showing the payment form again is confusing and breaks the login flow.
  if (user && user.payment_status === "paid") return <Navigate to="/team" replace />;

  const gateway = config?.active_gateway === "zoho" ? "zoho" : "razorpay";

  const startZohoPayment = async () => {
    const { data } = await api.post("/payment/zoho/session", { agreed: true });
    const ok = await loadZoho();
    if (!ok) { toast.error("Failed to load Zoho checkout"); setLoading(false); return; }
    const instance = new window.ZPayments({
      account_id: data.account_id,
      domain: data.domain,
      otherOptions: { api_key: data.api_key },
    });
    try {
      await instance.requestPaymentMethod({
        payments_session_id: data.payments_session_id,
        amount: data.amount,
        currency_code: data.currency || "INR",
        business: "IdeaThon · NoobSync Innovate",
        description: "Registration Fee (Non-refundable)",
      });
      // Confirm server-side regardless of the widget return shape.
      await api.post("/payment/zoho/verify", { payments_session_id: data.payments_session_id });
      toast.success("Payment confirmed! Welcome to IdeaThon.");
      track("payment_success", { funnel_step: 3, mode: "zoho" });
      trackPurchase(data.payments_session_id, fee, "INR");
      await refresh();
      navigate("/team");
    } catch (err) {
      // Surface a friendly message; the webhook backstop still confirms a real payment.
      const code = err?.code || err?.message || "";
      if (String(code).toLowerCase().includes("cancel")) toast.error("Payment cancelled.");
      else toast.error(errorMessage(err) || "Payment could not be completed.");
    } finally {
      try { await instance.close(); } catch { /* ignore */ }
      setLoading(false);
    }
  };

  const startPayment = async () => {
    if (!agreed) return toast.error("Please agree to the no-refund policy");
    // Guard against a lost session (e.g. private/in-app browser dropped the cookie).
    if (!user) {
      toast.error("Your session expired. Please log in again to continue.");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      if (gateway === "zoho") {
        await startZohoPayment();
        return;
      }
      const { data: order } = await api.post("/payment/create-order", { agreed: true });

      // FREE / 100%-off referral flow — backend already marked the user paid, no gateway.
      if (order.free) {
        toast.success("Referral applied — your entry is free! Welcome to IdeaThon.");
        track("payment_success", { funnel_step: 3, mode: "free" });
        if (order.payment_id) trackPurchase(order.payment_id, 0, "INR");
        await refresh();
        navigate("/team");
        return;
      }

      // If mock mode (no real Razorpay keys), confirm immediately for dev
      if (order.mock) {
        await api.post("/payment/verify", {
          razorpay_order_id: order.order_id,
          razorpay_payment_id: `pay_mock_${Date.now()}`,
          razorpay_signature: "",
        });
        toast.success("Payment received (test mode). Welcome to IdeaThon!");
        track("payment_success", { funnel_step: 3, mode: "test" });
        trackPurchase(`pay_mock_order_${order.order_id}`, order.value ?? finalPrice, "INR");
        await refresh();
        navigate("/team");
        return;
      }

      const ok = await loadRazorpay();
      if (!ok) { toast.error("Failed to load Razorpay checkout"); setLoading(false); return; }

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "IdeaThon · NoobSync Innovate",
        description: "Registration Fee (Non-refundable)",
        theme: { color: "#FF6B2B" },
        prefill: { name: user?.name, email: user?.college_email },
        handler: async (resp) => {
          try {
            await api.post("/payment/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment confirmed! Welcome to IdeaThon.");
            track("payment_success", { funnel_step: 3, mode: "live" });
            trackPurchase(resp.razorpay_payment_id, order.value ?? finalPrice, "INR");
            await refresh();
            navigate("/team");
          } catch (err) {
            toast.error(errorMessage(err));
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error?.description || "Try again"}`);
        setLoading(false);
      });
      rzp.open();
      // Meta InitiateCheckout — fired only now that Razorpay checkout has actually opened.
      // Deduped with the server CAPI via the backend-generated order.meta_event_id.
      if (order.meta_event_id) {
        trackMetaEventWithId("InitiateCheckout", order.meta_event_id, {
          content_type: "product",
          content_category: "IdeaThon Entry",
          content_name: "IdeaThon 2026 Registration",
          content_ids: ["ideathon-2026-entry"],
          currency: order.currency || "INR",
          value: order.value ?? fee,
          num_items: order.num_items ?? 1,
        });
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="App" data-testid="payment-page">
      <Navbar />
      <div className="flex items-start justify-center px-6 pt-12 pb-16 sm:pt-16 sm:pb-20">
        <div className="w-full max-w-xl fade-up">
          <div className="label-eyebrow mb-3">Step 3 of 3</div>
          <h1 className="font-display text-4xl text-white">Complete your registration</h1>

          <div className="mt-8 card-surface border-l-2 border-brand">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow">IdeaThon Registration</div>
                <div className="font-display text-3xl text-white mt-1">For {user?.name || "you"}</div>
                <div className="text-sm text-gray-500 mt-1">{user?.college_email}</div>
              </div>
              <div className="text-right">
                <div className="label-eyebrow">Total</div>
                {perk?.valid && perk.final_price !== fee ? (
                  <>
                    <div className="text-lg text-gray-500 line-through leading-none" data-testid="payment-base-amount">₹{fee}</div>
                    <div className="font-display text-5xl text-brand" data-testid="payment-amount">{finalPrice <= 0 ? "FREE" : `₹${finalPrice}`}</div>
                  </>
                ) : (
                  <div className="font-display text-5xl text-brand" data-testid="payment-amount">₹{fee}</div>
                )}
              </div>
            </div>
            {perk?.valid && (
              <div className="mt-4 pt-4 border-t border-white/5" data-testid="payment-discount-note">
                <div className="flex items-center gap-2 text-sm text-emerald-400">
                  <Gift className="w-4 h-4" /> {perk.label || "Referral discount"} applied{perk.referrer_name ? ` (via ${perk.referrer_name})` : ""}
                </div>
                {perk.final_price < fee && (
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 text-xs font-semibold text-emerald-300" data-testid="payment-savings-badge">
                    You save ₹{perk.savings ?? (fee - perk.final_price)}
                    {perk.discount_type === "percent_off" && formatPercentOff(fee, perk.final_price) ? ` · ${formatPercentOff(fee, perk.final_price)} off` : ""}
                  </div>
                )}
              </div>
            )}
            {perk && !perk.valid && ["expired", "sold_out"].includes(perk.reason) && (
              <div className="mt-4 pt-4 border-t border-white/5 text-sm text-amber-400" data-testid="payment-discount-note">
                This referral offer is {perk.reason === "sold_out" ? "fully claimed" : "expired"} — full price applies.
              </div>
            )}
          </div>

          <div className="mt-5 card-surface" data-testid="referral-section">
            <div className="flex items-center gap-2 mb-1">
              <Gift className="w-4 h-4 text-brand" strokeWidth={1.5} />
              <span className="label-eyebrow">Brand Ambassador referral (optional)</span>
            </div>
            {applied ? (
              <div className="flex items-center gap-2 text-sm text-emerald-400 mt-2" data-testid="referral-applied">
                <Check className="w-4 h-4" /> Code <span className="font-mono text-white">{user.referral_code}</span> applied
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-3">Were you referred by a NoobSync Brand Ambassador? Enter their code before paying to credit them.</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={refCode}
                    onChange={(e) => setRefCode(e.target.value)}
                    placeholder="e.g. NSA-XXXX-1234"
                    className="input-field flex-1"
                    data-testid="payment-referral-input"
                  />
                  <button onClick={applyReferral} disabled={refBusy || !refCode.trim()} className="btn-secondary !px-4" data-testid="payment-referral-apply">
                    {refBusy ? <span className="spinner" /> : "Apply"}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="mt-5 p-5 border border-[#F59E0B]/40 bg-[#F59E0B]/5" data-testid="refund-warning">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
              <div className="text-sm">
                <div className="font-display text-white text-base mb-1">Strictly Non-Refundable</div>
                <p className="text-gray-300 leading-relaxed">This fee is <strong className="text-white">strictly non-refundable under any circumstances</strong>. By proceeding you agree to our no-refund policy — whether you withdraw, get disqualified, or the team falls below minimum size.</p>
              </div>
            </div>
          </div>

          <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="agree-label">
            <Checkbox
              checked={agreed}
              onCheckedChange={setAgreed}
              className="mt-0.5 border-white/30 data-[state=checked]:bg-brand data-[state=checked]:border-brand data-[state=checked]:text-black"
              data-testid="agree-checkbox"
            />
            <span className="text-sm text-gray-300 leading-relaxed">
              I have read and agree to the{" "}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-2 hover:text-white" data-testid="terms-link">Terms &amp; Conditions</a>
              {" "}and{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand underline underline-offset-2 hover:text-white" data-testid="privacy-link">Privacy Policy</a>,
              {" "}including the <strong className="text-white">strictly non-refundable ₹{fee} payment policy</strong>.
            </span>
          </label>

          <button onClick={startPayment} disabled={!agreed || loading || user?.payment_status === "paid"}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2" data-testid="pay-now-btn">
            {loading ? <span className="spinner" /> : <Lock className="w-4 h-4" strokeWidth={1.5} />}
            {user?.payment_status === "paid" ? "Already Paid" : isFree ? "Claim Free Entry" : "Pay Securely ₹" + finalPrice}
          </button>

          {!isFree && gateway !== "zoho" && (
            <p className="text-sm text-center text-gray-400 mt-4" data-testid="payment-razorpay-trust">Secure payments powered by Razorpay</p>
          )}
          <p className="text-xs text-center text-gray-500 mt-2">{isFree ? "100% referral discount · no payment required" : gateway === "zoho" ? "Secured by Zoho Payments · UPI, Cards, Net Banking" : "UPI, Cards, Net Banking, Wallets"}</p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
